import { QueryParams, RouteParams, RoutesPath, StateParams } from "@ui/navigation/routes";
import { isNotNil } from "@utils/assert";
import { NavigateOptions, useNavigate as useReactRouterNavigate } from "react-router";
import { useCallback } from "react";

export type UseNavigationHook = <R extends RoutesPath>(
	path: R,
	params?: Params<R>,
	options?: Omit<NavigateOptions, "state"> & { target?: "_blank" },
) => void;

export const useNavigate = (): UseNavigationHook => {
	const navigate = useReactRouterNavigate();
	return useCallback((path, params, options): void => {
		let pathWithParam = path as string;

		if (isNotNil(params)) {
			// Route params
			if (isNotNil(params.route)) {
				const routeParams = params.route as Record<string, unknown>;
				Object.entries(routeParams).forEach(([key, value]) => {
					if (typeof value === "string") {
						pathWithParam = pathWithParam.replace(`:${key}`, value);
					}
				});
			}

			// Query params
			if (isNotNil(params.query)) {
				const queryParams = params.query as Record<string, unknown>;
				const queryParamsKeyValues: [string, URIEncodable][] = [];
				Object.entries(queryParams).forEach(
					([key, value]) => isURIEncodable(value) && queryParamsKeyValues.push([key, value]),
				);
				pathWithParam += `?${queryParamsKeyValues
					.map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
					.join("&")}`;
			}
		}

		if (options?.target === "_blank") {
			window.open(pathWithParam, options.target);
		} else {
			navigate(pathWithParam, { ...options, state: params?.state });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
};

export const useGoBack = (): (() => void) => {
	const navigate = useReactRouterNavigate();
	return () => navigate(-1);
};

type URIEncodable = boolean | string | number;

const isURIEncodable = (item: unknown): item is URIEncodable =>
	typeof item === "string" || typeof item === "boolean" || typeof item === "number";

interface Params<R extends RoutesPath> {
	route?: R extends keyof RouteParams ? RouteParams[R] : undefined;
	query?: R extends keyof QueryParams ? QueryParams[R] : undefined;
	state?: R extends keyof StateParams ? StateParams[R] : undefined;
}
